import { ContextType, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useMachine } from '@xstate/react';
import { useCheckoutCartState } from '@monofront/business/contexts';
import { LIST_PAYMENT_METHODS } from '@monofront/business/queries';
import { paymentSelectionMachine } from '@monofront/business/stateMachines';
import {
  IListPaymentMethodsQueryResult,
  PaymentMethodSource,
  PaymentMethodsType,
} from '@monofront/types';
import { useAuthentication } from '@context/businessContainer';
import { usePaymentMethods } from '@features/PaymentMethods/PaymentMethodContext';
import useHandlePaymentError from '../hooks/useHandlePaymentErrors';
import { CheckoutPaymentContext } from './CheckoutPaymentContext';

/**
 * Checkout payment provider for the Checkout.com Flow layout. Same contract as
 * CheckoutPaymentProvider, but the selection slice is powered by the
 * paymentSelectionMachine (XState v5). The legacy Frames provider keeps its own
 * useState implementation; this one is deleted together with the Frames layout
 * once the A/B test ends.
 */
const CheckoutPaymentFlowProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { checkoutCart } = useCheckoutCartState();
  const { isLoading } = useAuthentication();
  const { handlePaymentError } = useHandlePaymentError();

  const [snapshot, send] = useMachine(paymentSelectionMachine);

  const [isOrderValidationInProgress, setIsOrderValidationInProgress] =
    useState(false);
  const [isFlowInitializing, setIsFlowInitializing] = useState(false);

  const { paymentMethods, isLoading: getExistingPaymentMethodsLoading } =
    usePaymentMethods();
  const {
    data: getNewPaymentOptionsQuery,
    loading: getNewPaymentOptionsLoading,
  } = useQuery<IListPaymentMethodsQueryResult>(LIST_PAYMENT_METHODS, {
    ssr: false,
    fetchPolicy: 'cache-and-network',
    variables: {
      tokenValue: checkoutCart?.id,
      paymentId: checkoutCart?.payment?.id,
    },
    skip: !checkoutCart?.id || !checkoutCart?.payment?.id,
    onError: (error) => {
      handlePaymentError({
        code: 'CANNOT_FETCH_NEW_PM',
        message: 'Cannot fetch listPaymentMethods',
        error,
      });
    },
  });

  const isPaymentPageReady =
    !getNewPaymentOptionsLoading &&
    !getExistingPaymentMethodsLoading &&
    !isLoading;

  const savedPaymentMethods = paymentMethods.filter(
    ({ paymentType, source }) =>
      paymentType !== PaymentMethodsType.BankAccount &&
      source !== PaymentMethodSource.STRIPE,
  );
  const newPaymentOptions = useMemo(
    () => getNewPaymentOptionsQuery?.listPaymentMethods || [],
    [getNewPaymentOptionsQuery?.listPaymentMethods],
  );

  const selection = snapshot.context;

  const value: ContextType<typeof CheckoutPaymentContext> = {
    isPaymentPageReady,
    isOrderValidationInProgress,
    savedPaymentMethods,
    newPaymentOptions,
    selectedPaymentRadioId: selection.radioId,
    selectedPaymentType: selection.paymentType,
    selectedExistingPaymentMethod: selection.existingPaymentMethod,
    selectedNewCheckoutComCard: selection.newCheckoutComCard,
    selectedNewPaypal: selection.newPaypal,
    selectedNewApplePay: selection.newApplePay,
    selectExistingPaymentMethod: (radioId, paymentMethod, paymentType) =>
      send({ type: 'SELECT_EXISTING', radioId, paymentMethod, paymentType }),
    selectNewCheckoutComOption: (radioId, paymentType) =>
      send({ type: 'SELECT_NEW_CARD', radioId, paymentType }),
    selectNewPaypalOption: (radioId, paypalMethod, paymentType) =>
      send({ type: 'SELECT_PAYPAL', radioId, paypalMethod, paymentType }),
    selectNewApplePayOption: (radioId, paymentType) =>
      send({ type: 'SELECT_APPLE_PAY', radioId, paymentType }),
    // Card readiness is driven by the Checkout.com widget in the Flow.
    updateNewCheckoutComCard: () => undefined,
    clearSelectedPaymentMethod: () => send({ type: 'CLEAR' }),
    setIsOrderValidationInProgress,
    isFlowInitializing,
    markFlowInitializing: () => setIsFlowInitializing(true),
    markFlowReady: () => setIsFlowInitializing(false),
  };

  return (
    <CheckoutPaymentContext.Provider value={value}>
      {children}
    </CheckoutPaymentContext.Provider>
  );
};

export { CheckoutPaymentFlowProvider };
